/**
 * Main Application Component
 * Handles routing and authentication state
 */

import { FC, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useRecoilValue } from 'recoil';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MainLayout } from '@components/layout';
import { LoginPage } from '@features/auth';
import { tokenState, guardState } from '@auth/authState';

// Lazy load pages for better performance
const DashboardPage = lazy(() => import('@features/dashboard/DashboardPage'));

// Permissions module routes
const PermissionsRoutes = lazy(() => import('@features/permissions/PermissionsRoutes'));

// Branding module routes
const BrandingRoutes = lazy(() => import('@features/branding/BrandingRoutes'));

// Billing module routes
const BillingRoutes = lazy(() => import('@features/billing/BillingRoutes'));

// ERP Parameters module routes
const ErpParametersRoutes = lazy(() => import('@features/erp-parameters/ErpParametersRoutes'));

const SettingsPage = lazy(() =>
  Promise.resolve({
    default: () => (
      <Box sx={{ p: 4 }}>
        <h1>Configuración</h1>
        <p>Módulo en desarrollo</p>
      </Box>
    ),
  })
);

// Loading fallback
const LoadingFallback: FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
    }}
  >
    <CircularProgress />
  </Box>
);

// Auth loading screen
const AuthLoadingScreen: FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#1a237e',
      backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    }}
  >
    <CircularProgress sx={{ color: 'white', mb: 2 }} />
    <Typography variant="body1" sx={{ color: 'white' }}>
      Verificando autenticación...
    </Typography>
  </Box>
);

// Protected Route wrapper
const ProtectedRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const token = useRecoilValue(tokenState);
  const guard = useRecoilValue(guardState);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for token AND guard before rendering content that needs API access
  if (!token || !guard.header) {
    return <LoadingFallback />;
  }

  return <MainLayout>{children}</MainLayout>;
};

export const App: FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // Show loading screen while MSAL is processing
  if (inProgress !== InteractionStatus.None) {
    return <AuthLoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/permissions/*"
          element={
            <ProtectedRoute>
              <PermissionsRoutes />
            </ProtectedRoute>
          }
        />

        <Route
          path="/billing/*"
          element={
            <ProtectedRoute>
              <BillingRoutes />
            </ProtectedRoute>
          }
        />

        <Route
          path="/branding/*"
          element={
            <ProtectedRoute>
              <BrandingRoutes />
            </ProtectedRoute>
          }
        />

        <Route
          path="/erp-parameters/*"
          element={
            <ProtectedRoute>
              <ErpParametersRoutes />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default App;
